import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';
import { FlowManagementService, distinct, flatten } from '../services/flow-management.service';
import { State, process, CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { GridDataResult, DataStateChangeEvent, FilterService } from '@progress/kendo-angular-grid';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { Flow } from '../obj/Flow';
import { colours } from '../constants';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { getTriggerIconUrl } from '../flows/trigger-icon.util';
import { StorageRouterService } from '../services/storage-router.service';
import { UserItem } from '../services/storage-types';

@Component({
    selector: 'app-flow-search',
    templateUrl: './flow-search.component.html',
    styleUrls: ['./flow-search.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class FlowSearchComponent implements OnInit, OnDestroy {

  constructor(
    protected flowManagementService: FlowManagementService,
    private storageService: StorageRouterService,
    private sanitizer: DomSanitizer
  ) { }

  public searchText: string = ''

  all: Flow[];
  flows: Flow[] = [];
  flowTags: string[] = [];
  flowTriggers: string[] = [];
  flowTriggerKinds: string[] = [];
  flowStates: string[] = [];
  allOwners: BehaviorSubject<any[]> = new BehaviorSubject([]);
  users$: BehaviorSubject<UserItem[]> = new BehaviorSubject([]);

  apiRoot: string = "https://flow.microsoft.com";
  environment: string;

  state: State = {
    skip: 0,
    take: 50, //this.flows.length,

    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: [] //[{ field: 'ProductName', operator: 'contains', value: 'Chef' }]
    },
    sort: [
      {
        'field': 'displayName',
        'dir': 'asc'
      }
    ]
  };
  view: GridDataResult = process(this.flows, this.state);
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  cache = {
    flows: 0,
    runs: 0,
    settings: 0
  };

  loaded: boolean = false;
  statusText: string = 'Search ready.';
  /** True while we're streaming definitions in for /search full-text. */
  public definitionsLoading = false;
  /** Progress counters surfaced to the template. */
  public definitionsLoaded = 0;
  public definitionsTotal = 0;
  private subscriptions = new Subscription();
  private ownerTextCache = new WeakMap<Flow, string>();
  private activeLoadId = 0;
  /** flowName → definition JSON pulled live for this /search session. Discarded on env change/destroy. */
  private definitionsByFlowName = new Map<string, any>();
  /** flowName → precomputed lowercase haystack (rebuilt when a definition arrives). */
  private haystackByFlowName = new Map<string, string>();

  @ViewChild('grid', {static: true}) grid;

  ngOnInit() {
    console.log('[search] ngOnInit');



    this.subscriptions.add(this.flowManagementService.serviceStatus$.subscribe((status) => {
      if (status) {
        this.statusText = status;
      }
    }));

    this.subscriptions.add(this.storageService.getUsers$().subscribe((users) => {
      console.log('[search] getUsers$ emitted', users?.length, 'users');
      this.users$.next(users || []);
      this.ownerTextCache = new WeakMap<Flow, string>();
      this.refreshOwnerFilters();
    }));

    this.subscriptions.add(combineLatest([this.flowManagementService.currentEnvironment$])
    .subscribe(([environment])=>{
      console.log('[search] currentEnvironment$ emitted', environment);

      if (environment) {
        this.refreshEnvironment(environment, true);
      }
    }));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  async refreshEnvironment(environment, force = false) {

    if (!force && environment == this.environment && this.loaded) {
      return;
    }

    const loadId = ++this.activeLoadId;
    this.loaded = false;
    this.environment = environment;
    // Wipe per-session full-text state — definitions are not persisted.
    this.definitionsByFlowName = new Map();
    this.haystackByFlowName = new Map();
    this.definitionsLoading = false;
    this.definitionsLoaded = 0;
    this.definitionsTotal = 0;
    try {
      console.log('[search] calling getFlowSummaries for env', environment);
      console.time('[search] load cached flow summaries');
      const flows = await this.storageService.getFlowSummaries(environment);
      console.log('[search] getFlowSummaries returned', flows?.length, 'rows');
      if (loadId !== this.activeLoadId) return;
      this.all = flows || [];
      console.timeEnd('[search] load cached flow summaries');
      this.ownerTextCache = new WeakMap<Flow, string>();
      this.applySearchResults([...(this.all || [])]);
      this.setStatus(`Loaded ${this.all.length} flows. Fetching definitions for full-text search…`);
      this.loaded = true;
      // Stream definitions in the background — search becomes full-text as they arrive.
      this.loadDefinitions(loadId).catch((err) => {
        console.warn('[FlowSearchComponent] loadDefinitions failed', err);
      });
    } catch (reason) {
      if (loadId !== this.activeLoadId) return;
      console.error('[FlowSearchComponent] cached search load failed', reason);
      this.all = [];
      this.flows = [];
      this.flowTags = [];
      this.flowTriggers = [];
      this.flowTriggerKinds = [];
      this.flowStates = [];
      this.view = process(this.flows, this.state);
      this.setStatus('Search cache load failed. Refresh /flows first.');
      this.loaded = true;
    }
  }

  /**
   * Live-fetch every flow's definition for this /search session and build the
   * lowercase haystack used by the full-text filter. Nothing is persisted; on
   * the next visit we fetch again. Cancels via the activeLoadId guard if the
   * env changes or the component is destroyed.
   */
  private async loadDefinitions(loadId: number): Promise<void> {
    const candidates = (this.all || []).filter((f) => this.canFetchDefinition(f));
    this.definitionsTotal = candidates.length;
    this.definitionsLoaded = 0;
    this.definitionsLoading = candidates.length > 0;

    if (!candidates.length) {
      this.setStatus(`Loaded ${this.all.length} flows. (No definitions available for full-text.)`);
      return;
    }

    const chunkSize = 8;
    for (let i = 0; i < candidates.length; i += chunkSize) {
      if (loadId !== this.activeLoadId) {
        this.definitionsLoading = false;
        return;
      }
      const chunk = candidates.slice(i, i + chunkSize);
      await this.loadDefinitionChunk(chunk, loadId);
      this.definitionsLoaded = Math.min(i + chunk.length, candidates.length);
      if (loadId !== this.activeLoadId) return;
      this.setStatus(`Loading definitions ${this.definitionsLoaded} / ${candidates.length}…`);
    }

    if (loadId !== this.activeLoadId) return;
    this.definitionsLoading = false;
    this.setStatus(`Full-text ready. ${this.all.length} flows, ${this.definitionsLoaded} definitions.`);
  }

  private async loadDefinitionChunk(chunk: Flow[], loadId: number): Promise<void> {
    const adminFlows = chunk.filter((flow) => this.shouldUseAdminScope(flow));
    const userFlows = chunk.filter((flow) => !this.shouldUseAdminScope(flow));
    await Promise.all([
      this.loadDefinitionGroup(userFlows, false, loadId),
      this.loadDefinitionGroup(adminFlows, true, loadId),
    ]);
  }

  private async loadDefinitionGroup(flows: Flow[], admin: boolean, loadId: number): Promise<void> {
    if (!flows.length) return;
    try {
      const details = await this.flowManagementService.getFlowsBatch(this.environment, flows.map((flow) => flow.flowName), admin);
      if (loadId !== this.activeLoadId) return;
      await Promise.all(flows.map((flow) => {
        const detail = details.get(flow.flowName);
        if (detail) {
          if (admin && this.flowManagementService.isDeletedFlowDetailMarker(detail)) {
            flow.flowState = 'Deleted';
            this.definitionsByFlowName.set(flow.flowName, null);
            this.haystackByFlowName.set(flow.flowName, this.buildHaystack(flow, null));
            return Promise.resolve();
          }
          this.storeDefinition(flow, detail);
          return Promise.resolve();
        }
        return this.loadDefinitionDirect(flow, admin, loadId);
      }));
    } catch (err) {
      console.warn('[FlowSearchComponent] batch definition fetch failed; falling back to direct requests', err);
      await Promise.all(flows.map((flow) => this.loadDefinitionDirect(flow, admin, loadId)));
    }
  }

  private async loadDefinitionDirect(flow: Flow, admin: boolean, loadId: number): Promise<void> {
    try {
      const detail = await this.flowManagementService.getFlow(flow.environment, flow.flowName, admin).toPromise();
      if (loadId !== this.activeLoadId) return;
      this.storeDefinition(flow, detail);
    } catch (err) {
      if (loadId !== this.activeLoadId) return;
      const status = Number((err as any)?.status || (err as any)?.error?.status || 0);
      const code = String((err as any)?.error?.error?.code || (err as any)?.error?.code || '');
      if (admin && status === 404 && code === 'FlowNotFound') {
        flow.flowState = 'Deleted';
      }
      console.warn('[FlowSearchComponent] definition fetch failed', flow.flowName, err);
      this.definitionsByFlowName.set(flow.flowName, null);
      this.haystackByFlowName.set(flow.flowName, this.buildHaystack(flow, null));
    }
  }

  private storeDefinition(flow: Flow, detail: any): void {
    const def = detail?.properties?.definition || null;
    this.definitionsByFlowName.set(flow.flowName, def);
    this.haystackByFlowName.set(flow.flowName, this.buildHaystack(flow, def));
  }

  /**
   * A flow's definition is fetchable if it came from the user listing
   * (my/shared) OR from the admin listing — both endpoints return
   * `properties.definition`. Graphql-only flows have no live API path and
   * must search on metadata alone.
   */
  private canFetchDefinition(flow: Flow): boolean {
    const sources = (flow?.sources || []).map((s) => (s || '').toLowerCase());
    if (sources.length === 0) return true; // unknown origin — try user scope
    return sources.includes('my') || sources.includes('shared') || sources.includes('admin');
  }

  /**
   * Use the admin-scoped getFlow endpoint when the flow only came back through
   * the admin listing (no user-scope origin). User-scope is preferred when
   * available because it doesn't require admin permission to read.
   */
  private shouldUseAdminScope(flow: Flow): boolean {
    const sources = (flow?.sources || []).map((s) => (s || '').toLowerCase());
    const hasUserScope = sources.includes('my') || sources.includes('shared');
    return !hasUserScope && sources.includes('admin');
  }

  private applySearchResults(results: Flow[]) {
      // Pure data update — does NOT change the load gate. `loaded` is owned by
      // refreshEnvironment (initial cache hydration) only.
      console.log('[search] applySearchResults', results?.length);
      this.flows = results || [];
      console.time('[search] build flowTags');
      this.flowTags = (this.all || this.flows || []).reduce((a, b): string[] => [...a, ...b.tags], []).filter(distinct).sort();
      console.timeEnd('[search] build flowTags');
      this.flowTriggers = this.flows.map(flow => flow.trigger).filter(distinct).sort();
      this.flowTriggerKinds = this.flows.map(flow => flow.triggerKind).filter(distinct).sort();
      this.flowStates = this.flows.map(flow => flow.flowState).filter(distinct).sort();
      console.time('[search] refreshOwnerFilters');
      this.refreshOwnerFilters();
      console.timeEnd('[search] refreshOwnerFilters');
      console.time('[search] kendo process');
      this.view = process(this.flows, this.state);
      console.timeEnd('[search] kendo process');
      console.log('[search] applySearchResults done');
  }

  private setStatus(message: string) {
    this.statusText = message;
    this.flowManagementService.serviceStatus$.next(message);
  }

  onKeyEvent(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.onSearch();
    }
  }

  async onRegexSearch() {
    let re: RegExp = null;
    const searchText = this.searchText || '';
    if (searchText.startsWith('/')) {
      const regexParts = searchText.match(/^\/(.*?)\/([gim]*)$/);
      if (regexParts) {
        const [, pattern, flags] = regexParts;
        try { re = new RegExp(pattern, flags); } catch { /* fall through to text */ }
      }
    } else {
      try { re = new RegExp(searchText, 'i'); } catch { /* invalid pattern */ }
    }
    return this.onSearch(re);
  }

  /**
   * Client-side metadata filter. Searches across displayName, flowName, tags,
   * trigger, triggerKind, flowState, owners, and the description. Operates on
   * `this.all` (already loaded from cache) — no network, no async, no main-thread
   * stalls past the filter loop.
   */
  async onSearch(re: RegExp = null) {
    const searchText = (this.searchText || '').trim();

    if (!re && !searchText) {
      this.applySearchResults([...(this.all || [])]);
      this.setStatus(`Showing all ${this.all?.length || 0} flows.`);
      return;
    }

    const all = this.all || [];
    const needle = searchText.toLowerCase();
    const matches = all.filter((flow) => {
      const haystack = this.flowHaystack(flow);
      return re ? re.test(haystack) : haystack.includes(needle);
    });

    this.applySearchResults(matches);
    const loadingNote = this.definitionsLoading
      ? ` (still loading definitions ${this.definitionsLoaded}/${this.definitionsTotal})`
      : '';
    this.setStatus(`Found ${matches.length} of ${all.length} flows.${loadingNote}`);
  }

  /**
   * Returns the cached haystack for a flow if its definition has been streamed
   * in; otherwise builds and caches a metadata-only haystack on the fly. Once
   * the definition arrives, `loadDefinitions` rewrites the entry with the full
   * (definition-inclusive) haystack so subsequent searches see it.
   */
  private flowHaystack(flow: Flow): string {
    const cached = this.haystackByFlowName.get(flow.flowName);
    if (cached !== undefined) return cached;
    const haystack = this.buildHaystack(flow, null);
    this.haystackByFlowName.set(flow.flowName, haystack);
    return haystack;
  }

  private buildHaystack(flow: Flow, definition: any): string {
    const parts = [
      flow.displayName,
      flow.flowName,
      flow.description,
      flow.flowState,
      flow.trigger,
      flow.triggerKind,
      (flow.tags || []).join(' '),
      this.resolveOwners(flow),
      definition ? JSON.stringify(definition) : '',
    ];
    return parts.filter(Boolean).join('\n').toLowerCase();
  }

  private refreshOwnerFilters() {
    const users = this.users$.value || [];
    const sourceFlows = (this.all?.length ? this.all : this.flows) || [];
    const ownerIds = sourceFlows
      .reduce((a, b): string[] => [...a, ...b.shareOwners], [])
      .filter(distinct)
      .sort();

    this.allOwners.next(ownerIds.map((id) => {
      const user = this.findUser(id, users);
      return {
        text: user ? this.formatUser(user, null, id) : id,
        value: id
      };
    }));
  }

  public resolveOwners(flow: Flow): string {
    if (!flow) return '';
    const cached = this.ownerTextCache.get(flow);
    if (cached !== undefined) return cached;

    let result = '';
    const ids = flow.ownerIds;
    if (ids.length === 0) {
      const oid = flow.creatorOid || this.flowManagementService.getCurrentUser()?.localAccountId || null;
      if (!oid) return '';

      const user = this.findUser(oid, this.users$.value);
      result = this.formatUser(user, this.flowManagementService.getCurrentUser(), oid);
      this.ownerTextCache.set(flow, result);
      return result;
    }

    result = ids.map((ownerId) => {
      const user = this.findUser(ownerId, this.users$.value);
      return this.formatUser(user, null, ownerId);
    }).join(', ');
    this.ownerTextCache.set(flow, result);
    return result;
  }

  private findUser(userid: string, users: UserItem[]): UserItem | undefined {
    const id = (userid || '').toLowerCase();
    return (users || []).find(u => (u.key || '').toLowerCase().endsWith(id));
  }

  private formatUser(user: any, account?: any, fallbackId?: string): string {
    const name = user?.displayName || account?.name || '';
    const email = user?.email || account?.username || '';
    if (name && email) return `${name} <${email}>`;
    return name || email || fallbackId || '';
  }

  public hasSingleOwner(flow: Flow): boolean {
    if (flow.owners?.length === 1) return true;
    const st = flow.sharingType || '';
    if (st.includes('Coauthor') || st.includes('DelegatedAuth')) return false;
    if (!flow.owners || flow.owners.length === 0) {
      if (flow.creatorOid) return true;
      if (flow.userType === 'Owner' && !st.includes('CommonDataService')) return true;
    }
    return false;
  }

  public bgColor(flow: Flow) {
    var tags = flow.tags || [];

    for (var i = 0; i < tags.length; i++) {
      var tag = tags[i].replace('#', '').toLowerCase();

      if (typeof colours[tag] != 'undefined') {
        //return tag;
        return this.sanitizer.bypassSecurityTrustStyle(tag);
      }
    }
    return null;
  }

  getSafeUrl(url): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  private triggerIconSafeCache = new Map<string, { rawUrl: string; safeUrl: SafeUrl }>();
  public getTriggerIcon(flow: Flow): SafeUrl {
    const url = getTriggerIconUrl(flow);
    const key = flow?.flowName || (flow as any)?.name || JSON.stringify(flow?.triggers || []);
    const cached = this.triggerIconSafeCache.get(key);
    if (cached && cached.rawUrl === url) return cached.safeUrl;
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    this.triggerIconSafeCache.set(key, { rawUrl: url, safeUrl });
    return safeUrl;
  }
  /**
   * `ready` gates the loading overlay. True only once `refreshEnvironment`
   * has finished (success OR failure both flip it). Search-text changes and
   * definition refreshes do NOT flip it — those have their own UI cues.
   */
  get ready(): boolean {
    const hasEnvironment = !!(this.environment || this.flowManagementService.currentEnvironment);
    return hasEnvironment && this.loaded;
  }

  public dataStateChange(state: DataStateChangeEvent): void {

    if (state.sort && state.sort.length == 1) {
      // only on 1

      var field = state.sort[0].field;

      if (field != this.state.sort[0].field) {

        var desc = this.grid.columns.filter(function (c) { return c.field == field && (c.filter == "date" || c.filter == "number"); }).length > 0;

        if (desc) {
          state.sort[0].dir = "desc";
        }
      }
    }

    this.state = state;

    // setTimeout(()=>{
    //     this.storageDexieService.updateSettings({
    //         '_id': Settings.SETTING_GRIDSTATE,
    //         value: this.state
    //     });    
    // },1);
    this.view = process(this.flows, this.state);
  }

  filterByTag(tag: string) {
    var state: any = this.state;
    if (tag) {
      if (state.filter.filters && state.filter.filters.length && state.filter.filters[0].value == tag) {
        state.filter.filters = [];
      }
      else {
        state.filter.filters = [{
          "field": "tags",
          "operator": "contains",
          "value": tag
        }];
      }
    }
    else {
      state.filter.filters = [];
    }
    this.dataStateChange(state);
  }

  filterByTrigger(trigger: string) {
    var state: any = this.state;
    if (trigger) {
      if (state.filter.filters && state.filter.filters.length && state.filter.filters[0].value == trigger) {
        state.filter.filters = [];
      }
      else {
        state.filter.filters = [{
          "field": "trigger",
          "operator": "contains",
          "value": trigger
        }];
      }
    }
    else {
      state.filter.filters = [];
    }
    this.dataStateChange(state);
  }

  filterBy(field: string, value: string, toggle: boolean = true) {
    var state: any = this.state;
    if (value) {
      if (state.filter.filters && state.filter.filters.length && state.filter.filters[0].value == value && toggle) {
        state.filter.filters = [];
      }
      else {
        state.filter.filters = [{
          "field": field,
          "operator": "contains",
          "value": value
        }];
      }
    }
    else {
      state.filter.filters = [];
    }
    this.dataStateChange(state);
  }

  filterByTriggerKind(triggerKind: string) {
    this.filterBy("triggerKind", triggerKind);
  }

  filterByFlowState(flowState: string) {
    this.filterBy("flowState", flowState);
  }

  clearFilter() {
    var state: any = this.state;
    state.filter.filters = [];
    this.dataStateChange(state);
  }

  get hasFilter() {
    var hasFilter = false;

    try {
      hasFilter = this.state.filter.filters.length > 0;
    }
    catch (e) {
      // nothing.
    }

    return hasFilter;
  }

  public multiChange(values: any[], filterService: FilterService, field: string, operator: string): void {
    filterService.filter({
        filters: values.map(value => ({
            field: field,
            operator: operator,
            value
        })),
        logic: 'or'
    });
  }

  public multiFilters(filter: CompositeFilterDescriptor): FilterDescriptor[] {
    return flatten(filter).map(({ value }) => value);
  }

}
